import { useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';

import { useCarNavigation } from '../../hooks/useCarNavigation';
import * as mobileMap from '../../mappings/mobile_mappings';

interface Option {
  key: string;
  name: string;
}

function getModelOptions(brandKey: string): Option[] {
  const brandModels = mobileMap.MOBILE_MODELS[brandKey] ?? {};
  const brandVariations = mobileMap.MOBILE_VARIATIONS[brandKey] ?? {};
  const options: Option[] = [];

  for (const [modelKey, modelName] of Object.entries(brandModels)) {
    const variations = brandVariations[modelKey];
    if (variations?.length) {
      options.push(...variations.map((variation) => ({ key: modelKey, name: variation })));
    } else {
      options.push({ key: modelKey, name: modelName });
    }
  }

  return options;
}

export default function SearchBar() {
  const { goToCarDetails } = useCarNavigation();
  const [activeBrand, setActiveBrand] = useState<Option | null>(null);
  const [activeModel, setActiveModel] = useState<Option | null>(null);

  const brandOptions = useMemo<Option[]>(
    () => Object.keys(mobileMap.MOBILE_MODELS).map((key) => ({ key, name: mobileMap.MOBILE_BRANDS[key] })),
    [],
  );

  const modelOptions = useMemo<Option[]>(
    () => (activeBrand ? getModelOptions(activeBrand.key) : []),
    [activeBrand],
  );

  const fieldSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      '& fieldset': { border: 'none' },
      '&:hover, &.Mui-focused': { backgroundColor: 'action.hover' },
    },
  };

  return (
    <Paper elevation={8} sx={{ width: '100%', maxWidth: 720, p: 1, borderRadius: 3, border: 'none' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems="stretch"
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', sm: 'block' }, my: 1 }}
          />
        }
      >
        <Autocomplete
          sx={fieldSx}
          options={brandOptions}
          groupBy={(option) => option.name[0]}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          value={activeBrand}
          onChange={(_event, newValue) => {
            setActiveBrand(newValue);
            setActiveModel(null);
          }}
          renderInput={(params) => <TextField {...params} label="Brand" variant="outlined" />}
        />

        <Autocomplete
          sx={fieldSx}
          disabled={!activeBrand}
          options={modelOptions}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          value={activeModel}
          onChange={(_event, newValue) => setActiveModel(newValue)}
          renderInput={(params) => <TextField {...params} label="Model" variant="outlined" />}
        />

        <Button
          variant="contained"
          color="secondary"
          size="large"
          startIcon={<SearchIcon />}
          disabled={!activeBrand || !activeModel}
          onClick={() => {
            if (activeBrand && activeModel) {
              goToCarDetails(activeBrand.name, activeModel.name);
            }
          }}
          sx={{ flexShrink: 0, mx: { xs: 0, sm: 0.5 }, mt: { xs: 0.5, sm: 0 }, px: 3, alignSelf: 'center' }}
        >
          Search
        </Button>
      </Stack>
    </Paper>
  );
}
